/**
 * Disk use by the server's databases.
 *
 * Flight logs are kept forever now, so nothing on the server caps their growth
 * any more. This is how that growth stays visible to the people who can act on
 * it: tactical and above, who are also the only ones the server answers.
 */

import { api, type ApiResult } from '@/api/client';

export interface DatabaseSize {
  name: string;
  bytes: number;
}

export interface StorageReport {
  databases: DatabaseSize[];
  totalBytes: number;
  /** Null when the server could not read the volume. */
  diskFreeBytes: number | null;
  diskTotalBytes: number | null;
  retention: {
    /** Null means kept forever. */
    flightsDays: number | null;
    auditDays: number | null;
    standbyTelemetryDays: number | null;
  };
}

export function getStorage(): Promise<ApiResult<StorageReport>> {
  return api.get<StorageReport>('/fleet/storage', { logged: false });
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return '---';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}
